import { h, Component } from 'preact';
import cx from 'classnames';
import { PhotoEditService } from '../../../utils/photo-edit.service';
import { RequestState, REQUEST_SUCCESS, KumitaMultimedia } from '../../../../types';
import { hopplerApi } from '../../../../constants';
import { UploadablePhoto } from '../../../../types/uploadable-photo.model';

interface PostPropertyPhotoProps {
  onReviewStateChange?: (inReview: boolean) => void;

  photosForUpload: UploadablePhoto[];
  onPhotoSelectionChange?: (photos: UploadablePhoto[]) => void;
  hasUploaded?: boolean;
  photoUploadState?: RequestState;

  uploadedPhotos?: KumitaMultimedia[];
  onUploadedPhotosChange?: (photos: KumitaMultimedia[]) => void;
}

interface PostPropertyPhotoState {
  processedPhotos: UploadablePhoto[];
  isUploadBoxLit?: boolean;
}



export class PostPropertyPhoto extends Component<PostPropertyPhotoProps, PostPropertyPhotoState> {
  photoEditService: PhotoEditService;
  
  constructor(props: PostPropertyPhotoProps) {
    super(props);
    this.photoEditService = new PhotoEditService();

    this.setState({
      processedPhotos: props.photosForUpload || []
    });
  }

  componentWillReceiveProps(newProps: PostPropertyPhotoProps) {
    this.setState({ processedPhotos: newProps.photosForUpload || [] });
  }

  handleFileSelection(fileList: FileList) {
    const files: File[] = [];

    for (let i = 0; i < fileList.length; i++) {
      files.push(fileList[i]);
    }

    this.constructPhotoUrls(files).then((photos) => {
      this.setState({
        processedPhotos: photos
      });
      this.props.onReviewStateChange(true);

      if (this.props.onPhotoSelectionChange) {
        this.props.onPhotoSelectionChange(photos);
      }
    });
  }

  onFileInputChange = (e: Event) => {
    const targetInput = e.target as HTMLInputElement;
    this.handleFileSelection(targetInput.files);
  }

  deleteUploadedPhoto = (index: number) => {
    const copy = this.props.uploadedPhotos.map(photo => Object.assign({}, photo));
    copy.splice(index, 1);

    // If the primary photo was just deleted, mark the next one as primary.
    // The API will always treat the zeroth photo as the primary photo, so
    // this merely syncs up the labels we show with what the API will do.
    if (index == 0 && copy.length >= 1) {
      copy[0].isPrimary = 1;
    }

    this.props.onUploadedPhotosChange(copy);
  }

  changePrimaryPhoto = (index: number) => {
    const photos = this.props.uploadedPhotos.map(photo => Object.assign({}, photo));
    photos.forEach(photo => photo.isPrimary = 0);

    const primaryPhoto = photos.splice(index, 1)[0];
    primaryPhoto.isPrimary = 1;

    const reordered = [ primaryPhoto, ...photos ];
    this.props.onUploadedPhotosChange(reordered);
  }

  createPhotoObject(file: Blob): UploadablePhoto {
    const url = URL.createObjectURL(file);
    return { url, file };
  }

  constructPhotoUrls(files: File[]) {
    let photos: UploadablePhoto[] = [];

    let promise = new Promise<UploadablePhoto[]>(
      (resolve) => {
        // recursive function
        let resize = (index) => {
          if (index >= files.length) {
            resolve(photos);
            return;
          }

          const photo = this.createPhotoObject(files[index]);
          const canvas = this.photoEditService.getCanvas();
          let img = new Image;

          img.src = photo.url;

          img.onload = () => {
            const imgPromise = this.photoEditService.restrictImageSize(canvas, img);
            const thumbPromise = this.photoEditService.createThumbnail(canvas, img);

            Promise.all([imgPromise, thumbPromise]).then(
              (images) => {
                URL.revokeObjectURL(photo.url);

                photo.url = URL.createObjectURL(images[0]);
                photo.thumbnail = URL.createObjectURL(images[1]);
                photo.file = images[0];
                photos.push(photo);

                resize(index + 1);
              }
            );

          }

        }

        resize(0);
      }
    );

    return promise;
  }

  deletePhoto = (index) => {
    let photos = this.state.processedPhotos;
    
    photos.splice(index, 1);

    this.setState({
      processedPhotos: photos
    });

    if (photos.length < 1) {
      this.props.onReviewStateChange(false);
    }

  }

  rotateLeft = (photo: UploadablePhoto, index: number) => {
    const canvas = this.photoEditService.getCanvas();
    let img = new Image;

    img.src = photo.url;
    img.onload = () => {
      const promise = this.photoEditService.rotateImageLeft(canvas, img);

      promise.then(([thumbnail, rotatedImage]) => {
        let member = this.state.processedPhotos[index];
  
        URL.revokeObjectURL(member.url);
        URL.revokeObjectURL(member.thumbnail);
  
        member.url = URL.createObjectURL(rotatedImage);
        member.thumbnail = URL.createObjectURL(thumbnail);
        member.file = rotatedImage;
  
        const updatedPhotos = this.state.processedPhotos;
        this.setState({
          processedPhotos: updatedPhotos
        });
  
      });

    }
  }

  rotateRight = (photo: UploadablePhoto, index: number) => {
    const canvas = this.photoEditService.getCanvas();
    let img = new Image;

    img.src = photo.url;
    img.onload = () => {
      const promise = this.photoEditService.rotateImageRight(canvas, img);

      promise.then(([thumbnail, rotatedImage]) => {
        let member = this.state.processedPhotos[index];
  
        URL.revokeObjectURL(member.url);
        URL.revokeObjectURL(member.thumbnail);
  
        member.url = URL.createObjectURL(rotatedImage);
        member.thumbnail = URL.createObjectURL(thumbnail);
        member.file = rotatedImage;
  
        const updatedPhotos = this.state.processedPhotos;
        this.setState({
          processedPhotos: updatedPhotos
        });
  
      });

    }
  }

  handleDrop(e: DragEvent) {
    e.preventDefault();
    e.stopPropagation();

    this.setState({
      isUploadBoxLit: false
    });

    let fileList = e.dataTransfer.files;
    this.handleFileSelection(fileList);
  }

  handleDragMouseover(e) {
    e.preventDefault();
    e.stopPropagation();

    this.setState({
      isUploadBoxLit: true
    });
  }

  handleDragMouseout(e) {
    e.preventDefault();
    e.stopPropagation();

    this.setState({
      isUploadBoxLit: false
    });
  }

  getImageUrl(member: KumitaMultimedia) {
    if (member.url) {
      return member.url;
    } else if (member.media) {
      return `${hopplerApi.hoppimg}/${member.media}`;
    }
  }

  getUploadBlock(props: PostPropertyPhotoProps,state: PostPropertyPhotoState) {
    if (state.processedPhotos.length == 0) {
      return (
        <div className="">
          <div className={ cx(
              'hi-postprop__tab-content__photos__upload-box',
              {
                'lit': state.isUploadBoxLit
              }
            )}
              onDrop={(e) => this.handleDrop(e)}
              onDragEnter={(e) => this.handleDragMouseover(e)}
              onDragOver={(e) => this.handleDragMouseover(e)}
              onDragLeave={(e) => this.handleDragMouseout(e)}
              >
            <i className="fas fa-file-upload d-block"></i>
            <p className="mt-3 mb-2">
              drag and drop photos
            </p>
            <p className="mb-2">
              or
            </p>
            <div className="hi-postprop__tab-content__photos__upload-box__button">
              <input type="file" className="" multiple onChange={this.onFileInputChange}/>
              <button className="btn btn-outline-dark">
                Browse
              </button>
            </div>
            
          </div>
          <div className="hi-postprop__tab-content__note">
            <b>Note</b>: Please add at least three photos of the property. Photos should have at least 960px x 960px dimensions. No watermarks and collages.
          </div>

          <div className="row mt-4">
            {
              props.hasUploaded && props.uploadedPhotos ?

                props.uploadedPhotos.map((member, index) => {
                  
                  return (
                    <div className="col-6 mb-3">
                      <div className="hi-postprop__tab-content__photos__uploaded-image">
                        <div className="position-relative">
                          <img src={this.getImageUrl(member)} alt=""/>
                          { 
                            member.isPrimary ? 
                              <div className="hi-postprop__tab-content__photos__uploaded-image__primary">
                                Primary Photo
                              </div> : null
                          }
                        </div>
                        <div className="hi-postprop__tab-content__photos__uploaded-image__actions">
                          <button type="button" className="btn" disabled={ !!member.isPrimary }
                            onClick={(e) => this.changePrimaryPhoto(index)}>
                            SET AS PRIMARY PHOTO
                          </button>
                          <button type="button" className="btn" onClick={(e) => this.deleteUploadedPhoto(index)}>
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  );

                }) : null
            }
          </div>
        </div>
      );
    }
  }

  getReviewBlock(props: PostPropertyPhotoProps, state: PostPropertyPhotoState) {
    if (state.processedPhotos.length !== 0) {
      return (
        <div className="">
          <div className="row">
            {
              state.processedPhotos.map((member, index) => (
                <div className="col-12 col-md-4">
                  <div className="hi-image-edit-thumb mb-4">
                    <img className="" src={member.url} alt=""/>  
                    <div className="hi-image-edit-thumb__controls">
                      <button type="button" className="btn" onClick={(e) => this.deletePhoto(index)}>
                        <i className="fas fa-trash"></i>
                      </button>
                      <button type="button" className="btn" onClick={(e) => this.rotateLeft(member, index)}>
                        <i className="fas fa-undo"></i>
                      </button>
                      <button type="button" className="btn" onClick={(e) => this.rotateRight(member, index)}>
                        <i className="fas fa-redo"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ))
            }
          </div>
        </div>
      );
    }
  }

  render(props: PostPropertyPhotoProps, state: PostPropertyPhotoState) {
    return(
      <div className="hi-postprop__tab-content__photos">

        { this.getUploadBlock(props, state) }
        
        { this.getReviewBlock(props, state) }

        <div className="hi-postprop__tab-content__photos__canvas">
          <canvas id="hi-photo-edit-canvas" className="d-none"></canvas>
        </div>
      </div>
    );
  }
}